// Search slice for Redux Toolkit
//
// Filters/results/hasSearched live here (not component useState) specifically
// so navigating Search -> Viewer -> back to Search restores the previous
// search instead of wiping it and forcing another API call. The Search page
// itself is responsible for calling clearSearch() when the user navigates
// away to anywhere other than a viewer (see Search.js's unmount effect) -
// this slice only stores whatever it's told to.

import { createSlice } from '@reduxjs/toolkit';

export const DEFAULT_SEARCH_FILTERS = {
  customerNameCash: '',
  customerNameAccount: '',
  registration: '',
  partNumber: '',
  itemDescription: '',
  cashSaleNumber: '',
  refundNumber: '',
  invoiceNumber: '',
  creditNoteNumber: '',
  orderNumber: '',
  startDate: '',
  endDate: ''
};

const MAX_RECENT_SEARCHES = 10;
const RECENT_SEARCHES_STORAGE_KEY = 'bap_recent_searches';

// Recent searches survive across sessions (unlike filters/results, which are
// only kept for the duration of a Search -> Viewer -> back round trip), so
// they're backed by localStorage rather than just Redux state.
const loadRecentSearches = () => {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    return [];
  }
};

const saveRecentSearches = (searches) => {
  try {
    localStorage.setItem(RECENT_SEARCHES_STORAGE_KEY, JSON.stringify(searches));
  } catch (error) {
    // Storage unavailable/full - recent searches just won't persist, not fatal.
  }
};

const filtersAreEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const initialState = {
  filters: DEFAULT_SEARCH_FILTERS,
  results: [],
  hasSearched: false,
  recentSearches: loadRecentSearches()
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setSearchFilters: (state, action) => {
      state.filters = action.payload;
    },
    setSearchResults: (state, action) => {
      state.results = action.payload;
      state.hasSearched = true;
    },
    clearSearch: (state) => {
      state.filters = DEFAULT_SEARCH_FILTERS;
      state.results = [];
      state.hasSearched = false;
    },
    addRecentSearch: (state, action) => {
      const { filters, resultCount } = action.payload;
      const withoutDuplicate = state.recentSearches.filter(
        (entry) => !filtersAreEqual(entry.filters, filters)
      );
      const entry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        filters,
        resultCount,
        timestamp: new Date().toISOString()
      };
      state.recentSearches = [entry, ...withoutDuplicate].slice(0, MAX_RECENT_SEARCHES);
      saveRecentSearches(state.recentSearches);
    }
  }
});

export const { setSearchFilters, setSearchResults, clearSearch, addRecentSearch } = searchSlice.actions;
export default searchSlice.reducer;

// Selectors
export const selectSearchFilters = (state) => state.search.filters;
export const selectSearchResults = (state) => state.search.results;
export const selectHasSearched = (state) => state.search.hasSearched;
export const selectRecentSearches = (state) => state.search.recentSearches;
